import { Platform } from 'react-native';
import type { Theme } from '../theme';

/* Web-only page chrome. React Native styles cannot reach scroll bars, focus rings or the page's own
   scrolling, so on the web build we inject one small stylesheet and keep it in step with the theme.
   Native builds return at the first line.

   - Scroll bars: thin, theme-coloured, no arrow buttons (the browser default is a white 17px bar).
   - The message box: no scroll bar of its own, no browser focus ring.
   - The page never scrolls sideways. Focusing an input inside an `overflow: hidden` page can scroll it
     programmatically, which shifted the whole app left; `overflow: clip` forbids that, and a guard
     resets any stray horizontal offset on browsers that lack `clip`.
   - A field inside a box (the people picker) gets its focus ring on the box, not on the bare input in it.
   - Browser autofill: no pale blue wash over the field, no black text on the dark theme. The colour the
     browser wants to paint is put behind a transition that never ends, so it never shows. */

const STYLE_ID = 'helm-web-chrome';
export const SHELL_ID = 'helm-shell';
export const SHELL_MAIN_ID = 'helm-shell-main';
let guarded = false;

export function applyWebChrome(theme: Theme): void {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;

  const light = theme.name === 'light';
  const thumb = light ? 'rgba(16,20,40,0.22)' : 'rgba(255,255,255,0.16)';
  const thumbHover = light ? 'rgba(16,20,40,0.40)' : 'rgba(255,255,255,0.32)';

  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = `
html, body, #root, #${SHELL_ID}, #${SHELL_MAIN_ID} { overflow: clip; overscroll-behavior: none; }
html, body { background: ${theme.bg}; }

::-webkit-scrollbar { width: 10px; height: 10px; background: transparent; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: ${thumb}; border-radius: 10px; border: 3px solid transparent; background-clip: padding-box; }
::-webkit-scrollbar-thumb:hover { background: ${thumbHover}; border: 3px solid transparent; background-clip: padding-box; }
::-webkit-scrollbar-button { display: none; width: 0; height: 0; }
::-webkit-scrollbar-corner { background: transparent; }
@supports not selector(::-webkit-scrollbar) {
  * { scrollbar-width: thin; scrollbar-color: ${thumb} transparent; }
}

textarea { scrollbar-width: none; resize: none; }
textarea::-webkit-scrollbar { display: none; }
textarea:focus { outline: none; }
input:focus { outline: none; }
input:focus-visible { outline: 1px solid ${theme.accentBorder}; outline-offset: 0; }
[data-box] input:focus-visible { outline: none; }
[data-box]:focus-within { border-color: ${theme.accentBorder}; }
input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus { -webkit-text-fill-color: ${theme.ink}; caret-color: ${theme.ink}; transition: background-color 2147483647s ease-out 0s; }
input:autofill { -webkit-text-fill-color: ${theme.ink}; caret-color: ${theme.ink}; filter: none; transition: background-color 2147483647s ease-out 0s; }
`;

  if (!guarded) {
    guarded = true;
    const reset = () => {
      if (window.scrollX) window.scrollTo(0, window.scrollY);
      for (const n of [document.documentElement, document.body, document.getElementById('root'), document.getElementById(SHELL_ID), document.getElementById(SHELL_MAIN_ID)]) {
        if (n && n.scrollLeft) n.scrollLeft = 0;
      }
    };
    window.addEventListener('scroll', reset, { passive: true, capture: true });
    window.addEventListener('focusin', () => requestAnimationFrame(reset));
    window.addEventListener('resize', reset, { passive: true });
  }
}
